/**
 * Light inline formatting for the text blocks of a native form.
 *
 * The choice here is deliberate. A full WYSIWYG editor would store HTML, and
 * HTML then has to be re-implemented for the generated PDF - tags, nesting,
 * sanitising, and a rendering path that can disagree with what the browser
 * showed. What somebody signs would depend on which renderer ran.
 *
 * So the content stays plain text with two marks, parsed by *this* function on
 * both sides. The browser preview and the signed document read the same string
 * through the same parser, which is the only way they cannot drift apart:
 *
 *     **negrita**      bold
 *     _cursiva_        italic
 *     \*  \_           a literal asterisk or underscore
 *
 * Alignment is a property of the block, not of the text, and lives in the
 * field's `config.align`.
 */
export const TEXT_ALIGNMENTS = ['left', 'center', 'right', 'justify'];
/**
 * Splits text into runs carrying their style. Newlines stay inside the runs;
 * the caller decides what a line break means in its medium.
 */
export function parseInlineMarkup(input) {
    const runs = [];
    let bold = false;
    let italic = false;
    let buffer = '';
    let index = 0;
    const flush = () => {
        if (buffer.length > 0) {
            runs.push({ text: buffer, bold, italic });
            buffer = '';
        }
    };
    while (index < input.length) {
        const char = input[index];
        const next = input[index + 1];
        // A backslash escapes the next character, so a document can contain a
        // literal asterisk without turning into markup.
        if (char === '\\' && (next === '*' || next === '_' || next === '\\')) {
            buffer += next;
            index += 2;
            continue;
        }
        if (char === '*' && next === '*') {
            flush();
            bold = !bold;
            index += 2;
            continue;
        }
        if (char === '_') {
            flush();
            italic = !italic;
            index += 1;
            continue;
        }
        buffer += char;
        index += 1;
    }
    flush();
    return runs;
}
/** The text without any markup, for places that cannot show style. */
export function stripInlineMarkup(input) {
    return parseInlineMarkup(input)
        .map((run) => run.text)
        .join('');
}
/**
 * Breaks runs into words and spaces, keeping each token's style.
 *
 * Laying a line out word by word is what lets the PDF change font mid-sentence
 * and still justify, centre or right-align the result.
 */
export function toStyledWords(runs) {
    const lines = [[]];
    for (const run of runs) {
        const pieces = run.text.split(/(\n|\s+)/);
        for (const piece of pieces) {
            if (piece === '')
                continue;
            if (piece === '\n') {
                lines.push([]);
                continue;
            }
            lines[lines.length - 1].push({
                text: piece,
                bold: run.bold,
                italic: run.italic,
                space: /^\s+$/.test(piece),
            });
        }
    }
    return lines;
}
/** Reads a block's alignment out of its stored config. */
export function alignmentOf(config) {
    const align = config.align;
    return typeof align === 'string' && TEXT_ALIGNMENTS.includes(align)
        ? align
        : 'left';
}
//# sourceMappingURL=rich-text.js.map